<template>
  <div class="tw-flex tw-flex-col tw-h-full" data-test="recruiter-details-panel">
    <div v-if="loading || error" tabindex="0">
      <div class="tw-text-right tw-p-3">
        <WdsButtonIcon sm data-test="recruiter-details-close" @click="emit('close')">
          <WdsIcon name="close" :size="16" />
        </WdsButtonIcon>
      </div>
      <div v-if="loading" class="tw-mt-[30%] tw-flex tw-justify-center">
        <WdsSpinner :size="40" indeterminate />
      </div>
      <WdsAlert v-else error class="tw-mx-4">
        {{ $t('An error occurred') }}
      </WdsAlert>
    </div>
    <div v-else-if="companyRecruiter" class="tw-flex tw-flex-col tw-h-full" tabindex="0">
      <div class="gradient">
        <WdsAvatar
          :path="recruiterAvatar"
          :initials="recruiterInitials"
          :alt="recruiterName"
          class="profile-avatar"
          lg
        />
        <div class="tw-float-right tw-p-3">
          <WdsButtonIcon sm data-test="recruiter-details-close" @click="emit('close')">
            <WdsIcon name="close" :size="16" />
          </WdsButtonIcon>
        </div>
      </div>

      <div class="detail-header" data-test="recruiter-details-header">
        <WdsStack direction-row justify-space-between gap-sm>
          <div class="tw-basis-2/3">
            <WdsStack direction-column justify-space-between gap-xs>
              <WdsText :text="recruiterName" :title="recruiterName" xl />
              <div>
                <WdsLabel gray subtle>{{ $t('Staffing Agency') }}</WdsLabel>
              </div>
              <WdsStack direction-row align-center gap-xs>
                <WdsIcon
                  v-if="recruiterMarketCode"
                  :size="16"
                  :name="`flag-${recruiterMarketCode.toLowerCase()}` as WdsIconNameEnum"
                />
                <WdsText :text="recruiterMarketName" large secondary />
              </WdsStack>
            </WdsStack>
          </div>

          <div class="tw-basis-1/3">
            <WdsStack direction-column gap-sm align-end>
              <WdsLabel
                subtle
                :success="companyRecruiter.status === 'ACTIVE'"
                :warning="companyRecruiter.status === 'INVITED'"
              >
                {{ statusLabel }}
              </WdsLabel>

              <WdsStack direction-column justify-end gap-xs align-end>
                <WdsStack direction-row align-center gap-xs>
                  <WdsText>
                    <span class="tw-font-semibold">{{ feeDisplay }}</span>
                  </WdsText>
                  <WdsText>
                    <span class="tw-font-semibold">{{ $t('fee') }}</span>
                  </WdsText>
                </WdsStack>

                <div class="tw-text-right">
                  <WdsText>
                    <span class="tw-font-semibold">
                      {{ $tc('N/A | :count days', companyRecruiter.recruiterOwnershipDays ?? 0) }}
                    </span>
                  </WdsText>
                  <WdsText :text="$t('Ownership period')" secondary />
                </div>
              </WdsStack>
            </WdsStack>
          </div>
        </WdsStack>

        <WdsStack direction-row gap-md align-center class="tw-mt-6">
          <WdsButton primary data-test="recruiter-details-edit" @click="openEditModal">
            {{ $t('Edit') }}
          </WdsButton>
          <DeleteCompanyRecruiter :company-recruiter-id="companyRecruiterId" @deleted="deleted" />

          <div class="tw-hidden">
            <EditCompanyRecruiter
              ref="editCompanyRecruiterRef"
              :company-recruiter-id="companyRecruiterId"
              :name="recruiterName"
              :email="companyRecruiter.email ?? ''"
              :recruiter-fee="companyRecruiter.recruiterFee ?? 0"
              :recruiter-ownership-days="companyRecruiter.recruiterOwnershipDays ?? undefined"
              :tags="companyRecruiter.tags?.map((t) => t.name) ?? []"
              :manages-workers="companyRecruiter.recruiterManagesWorkers"
              :has-hires="companyRecruiter.hasHires"
              @updated="updated"
            />
          </div>
        </WdsStack>
      </div>

      <WdsTabs
        v-model:active-tab-key="activeUrlTab"
        fitted
        secondary
        @update:active-tab-key="$emit('tab-changed')"
      >
        <WdsTab
          :title="$t('Profile')"
          tab-key="recruiter-profile-tab"
          class="tw-my-2 tw-mx-6"
          data-test="recruiter-profile-tab-button"
        >
          <RecruiterProfileTab
            v-if="!loading"
            :company-recruiter-id="companyRecruiterId"
            data-test="recruiter-profile-tab"
            @open-edit-modal="openEditModal"
          />
        </WdsTab>
        <WdsTab
          :title="$t('Activity')"
          tab-key="recruiter-activity-tab"
          class="tw-my-2 tw-mx-6"
          data-test="recruiter-activity-tab-button"
        >
          <RecruiterActivityTab
            v-if="!loading && recruiterId"
            :recruiter-id="recruiterId"
            :company-recruiter-id="companyRecruiterId"
            data-test="recruiter-activity-tab"
          />
        </WdsTab>
        <WdsTab
          :title="$t('Compliance')"
          tab-key="recruiter-compliance-tab"
          class="tw-my-2 tw-mx-6"
          data-test="recruiter-compliance-tab-button"
        >
          <RecruiterComplianceTab
            v-if="!loading && recruiterId"
            :recruiter-id="recruiterId"
            :company-recruiter-id="companyRecruiterId"
            data-test="recruiter-compliance-tab"
          />
        </WdsTab>
        <WdsTab
          :title="$t('Notes')"
          tab-key="recruiter-notes-tab"
          class="tw-my-2 tw-mx-6"
          data-test="recruiter-notes-tab-button"
        >
          <template #title>
            <WdsStack align-center gap-sm data-test="recruiter-notes-tab-title">
              {{ $t('Notes') }}
              <WdsLabel v-if="notesCount > 0" xs>{{ notesCount }}</WdsLabel>
            </WdsStack>
          </template>
          <RecruiterNotesTab
            v-if="!loading"
            :company-recruiter-id="companyRecruiterId"
            :account-id="accountId"
            data-test="recruiter-notes-tab"
            @update-notes="refetch"
          />
        </WdsTab>
      </WdsTabs>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsAlert,
  WdsTabs,
  WdsTab,
  WdsIcon,
  WdsStack,
  WdsButton,
  WdsButtonIcon,
  WdsText,
  WdsSpinner,
  WdsAvatar,
  WdsLabel,
  WdsIconNameEnum,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import upperFirst from 'lodash/upperFirst'
import { getAvatarPath } from '@/utils/avatar'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import { useFragment } from '@/gql'
import { COMPANY_RECRUITER_DETAIL_FRAGMENT } from '@/components/RecruiterDetailPanel/graphql/fragments'
import { CompanyRecruiterDetailQuery } from '@/components/RecruiterDetailPanel/graphql/queries'
import RecruiterProfileTab from '@/components/RecruiterDetailPanel/PanelTabs/RecruiterProfileTab.vue'
import RecruiterActivityTab from '@/components/RecruiterDetailPanel/PanelTabs/RecruiterActivityTab.vue'
import RecruiterComplianceTab from '@/components/RecruiterDetailPanel/PanelTabs/RecruiterComplianceTab.vue'
import RecruiterNotesTab from '@/components/RecruiterDetailPanel/PanelTabs/RecruiterNotesTab.vue'
import EditCompanyRecruiter from '@/components/Contacts/CompanyRecruiter/EditCompanyRecruiter.vue'
import DeleteCompanyRecruiter from '@/components/Contacts/CompanyRecruiter/DeleteCompanyRecruiter.vue'

const props = defineProps<{
  companyRecruiterId: string
  tabKey?: string
}>()

const emit = defineEmits(['close', 'updated', 'deleted', 'tab-changed'])

const activeUrlTab = ref(props.tabKey ?? 'recruiter-profile-tab')
const editCompanyRecruiterRef = ref<InstanceType<typeof EditCompanyRecruiter> | null>(null)

const variables = computed(() => ({
  companyRecruiterId: props.companyRecruiterId,
}))

const { loading, result, error, refetch } = useQuery(CompanyRecruiterDetailQuery, variables, {
  clientId: 'publicApi',
})

const companyRecruiter = computed(() =>
  useFragment(COMPANY_RECRUITER_DETAIL_FRAGMENT, result.value?.companyRecruiter),
)

const recruiterId = computed(() => companyRecruiter.value?.recruiter?.id)
const recruiterName = computed(() => companyRecruiter.value?.recruiter?.name ?? '')
const recruiterAvatar = computed(() =>
  getAvatarPath(companyRecruiter.value?.recruiter?.avatar, recruiterId.value ?? ''),
)
const recruiterInitials = computed(() => companyRecruiter.value?.recruiter?.initials ?? '')
const recruiterMarketCode = computed(() => companyRecruiter.value?.recruiter?.market?.code)
const recruiterMarketName = computed(() => companyRecruiter.value?.recruiter?.market?.name ?? '')
const statusLabel = computed(() => upperFirst((companyRecruiter.value?.status ?? '').toLowerCase()))
const feeDisplay = computed(() => {
  if (!companyRecruiter.value?.recruiterFee) {
    return 'N/A'
  }
  const fee = companyRecruiter.value?.recruiterFee ?? 0
  return `${(fee * 100).toFixed(0)}%`
})
const notesCount = computed(() => companyRecruiter.value?.notes?.paginatorInfo?.total ?? 0)
const accountId = currentAccount.globalId

const updated = async () => {
  await refetch()
  emit('updated')
}

const deleted = () => {
  emit('deleted')
  emit('close')
}

const openEditModal = () => {
  editCompanyRecruiterRef.value?.toggleShow()
}
</script>

<style scoped lang="scss">
.profile-avatar {
  @apply tw-w-20;
  @apply tw-h-20;
  @apply tw-top-[50px];
  @apply tw-absolute;
  @apply tw-ml-7;
}

.gradient {
  @apply tw-shrink-0;
  @apply tw-h-[100px];
  @apply tw-bg-gradient-to-r tw-from-indigo-50 tw-to-indigo-400;

  :deep(.wds-button-icon-container) {
    .wds-button-icon {
      @apply tw-bg-black/10;
    }

    &:hover {
      .wds-button-icon {
        @apply tw-bg-black/[0.14];
      }
    }
  }
}

.detail-header {
  @apply tw-px-7;
  @apply tw-pb-7;
  @apply tw-mt-8;
  @apply tw-pt-1.5;
}
</style>
